import ProgressiveFlow from '@atlassiansox/progressive-flow';
import { useRef } from 'react';
import PricingDisplay from './components/pricing-display/pricing-display';
import ProductFamilyContainer from './components/product-family-container';
import ProductSelectionList from './components/product-selection-list';
import { PRODUCT_FAMILY_ICONS_BY_NAME } from './constants';
import { ProductFamilyName } from './models';
import {
  calcLayoutStyles,
  containerStyle,
  innerContainerStyle,
  productSelectionHeaderStyle,
  stepDisplayStyle
} from './multi-product-calculator.styles';
import { useGlobalStore } from './store';

import { FormatMessage } from '@atlassiansox/bxp-ui-sdk';
import { breakpoints } from '@atlassiansox/wpl-utils';
import PriceTotalMobile from './components/pricing-display/price-total-mobile';
import { RestartAddProduct } from './components/restart-add-product/restart-add-product';
import { ModulePropsContext, ModulePropsV0 } from './module-props';
import { useSelectedProductFamilyNames } from './selectors';

const MultiProductCalculator = (props: ModulePropsV0) => {
  const {
    stepTitle,
    selectYourProductsLabel,
    addProductDetailsLabel,
    previousButtonLabel,
    nextButtonLabel,
    contactSalesTeamURL
  } = props;
  const [{ currentStep }, { selectStep }] = useGlobalStore();
  const [selectedProductFamilyNames] = useSelectedProductFamilyNames();

  const rootRef = useRef(null);
  const pricingDisplayRef = useRef(null);

  let maxWidth = currentStep === 0 ? '48rem' : '73.125rem';
  if ((window.visualViewport?.width || 0) <= breakpoints.md) {
    maxWidth = '100%'; // if we are in mobile view
  }

  return (
    <ModulePropsContext.Provider value={props}>
      {currentStep === 1 && <RestartAddProduct mobile={true} />}
      <div
        css={containerStyle}
        data-testid="multi-product-calculator"
        ref={rootRef}
      >
        <div css={innerContainerStyle(maxWidth)}>
          <h2 css={stepDisplayStyle}>
            <FormatMessage
              message={stepTitle}
              values={{
                step: `${currentStep + 1}`
              }}
            />
          </h2>
          <h3 css={productSelectionHeaderStyle}>
            {currentStep === 0
              ? selectYourProductsLabel
              : addProductDetailsLabel}
          </h3>
          <ProgressiveFlow
            screenIndex={currentStep}
            onNextButtonClick={() => {
              selectStep(1);
              window.scroll(0, 0);
            }}
            enableNextButton={
              currentStep < 1 && selectedProductFamilyNames.length > 0
            }
            showNextButton={currentStep < 1}
            nextButtonLabel={nextButtonLabel}
            previousButtonLabel={previousButtonLabel}
            maxWidth={maxWidth}
          >
            <ProductSelectionList />
            <div css={calcLayoutStyles}>
              <div>
                {selectedProductFamilyNames.map(
                  (productFamilyName: ProductFamilyName) => {
                    return (
                      <ProductFamilyContainer
                        name={productFamilyName}
                        key={productFamilyName}
                        icon={PRODUCT_FAMILY_ICONS_BY_NAME[productFamilyName]}
                      />
                    );
                  }
                )}
                <RestartAddProduct />
              </div>
              <div id="pricing-display" ref={pricingDisplayRef}>
                <PricingDisplay contactSalesTeamURL={contactSalesTeamURL} />
              </div>
              <PriceTotalMobile pricingDisplayRef={pricingDisplayRef} />
            </div>
          </ProgressiveFlow>
        </div>
      </div>
    </ModulePropsContext.Provider>
  );
};

export default MultiProductCalculator;
